// src/features/release-notes/components/ReleaseNotesPage.tsx

import React, { useCallback, useEffect, useState } from 'react';
import { useLocaleState, useTranslate } from 'react-admin';
import { Box, Skeleton } from '@mui/material';
import { CampaignRounded as NewsIcon } from '@mui/icons-material';
import { SectionHeader } from '@/components/common/SectionHeader';
import { EmptyState } from '@/components/common/EmptyState';
import { FetchErrorBanner } from '@/components/common/FetchErrorBanner';
import { manualLangFromLocale } from '@/features/manual/utils/labels';
import { releaseNotesApi } from '../api';
import type { ReleaseNote } from '../types';
import { useReleaseNoteActions } from '../useReleaseNoteActions';
import { ReleaseNoteCard } from './ReleaseNoteCard';

/** «Novedades»: every note published for this person, newest first; unacknowledged ones say «Nueva». */
export const ReleaseNotesPage: React.FC = () => {
  const translate = useTranslate();
  const [locale] = useLocaleState();
  const lang = manualLangFromLocale(locale);
  const [notes, setNotes] = useState<ReleaseNote[] | null>(null);
  const [failed, setFailed] = useState(false);
  const [attempt, setAttempt] = useState(0);
  const { openScreen, openManual } = useReleaseNoteActions();

  useEffect(() => {
    let active = true;
    setFailed(false);
    releaseNotesApi
      .history(lang)
      .then((result) => active && setNotes(Array.isArray(result) ? result : []))
      .catch(() => active && setFailed(true));
    return () => {
      active = false;
    };
  }, [lang, attempt]);

  const retry = useCallback(() => setAttempt((value) => value + 1), []);

  const renderContent = () => {
    if (failed) return <FetchErrorBanner onRetry={retry} />;
    if (!notes) {
      return (
        <Box aria-busy>
          <Skeleton variant="rectangular" height={140} sx={{ borderRadius: 1 }} />
          <Skeleton variant="rectangular" height={140} sx={{ mt: 2, borderRadius: 1 }} />
        </Box>
      );
    }
    if (notes.length === 0) return <EmptyState icon={<NewsIcon />} message={translate('releaseNotes.empty')} />;
    return (
      <Box component="ul" sx={{ listStyle: 'none', m: 0, p: 0, display: 'flex', flexDirection: 'column', gap: 2, maxWidth: 760 }}>
        {notes.map((note) => (
          <li key={note.id}>
            <ReleaseNoteCard note={note} showNew onOpenScreen={openScreen} onOpenManual={openManual} />
          </li>
        ))}
      </Box>
    );
  };

  return (
    <Box>
      <SectionHeader title={translate('releaseNotes.title')} subtitle={translate('releaseNotes.subtitle')} sticky={false} />
      <Box sx={{ mt: 2 }}>{renderContent()}</Box>
    </Box>
  );
};
